import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { adminAnalyticsAPI } from '../../services/api';
import PurchasesDetail from './PurchasesDetail';

const ARMY = '#4A5D3A';
const YELLOW = '#F5E642';
const MUTED_GREEN = '#6B8A52';
const GRAY = '#6B7280';

const PIE_COLORS = [GRAY, ARMY, '#DC2626'];

// A stat card that can optionally be clicked to drill down.
// When onClick is provided the card gets a hover highlight and cursor-pointer.
const StatCard = ({ label, value, sub, onClick }) => (
  <div
    className={[
      'bg-battle-gray border border-army/20 rounded-lg p-5 flex flex-col',
      onClick ? 'cursor-pointer hover:border-brand-yellow/60 transition-colors group' : '',
    ].join(' ')}
    onClick={onClick}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <span className={['text-gray-400 text-xs uppercase tracking-wider mb-1', onClick ? 'group-hover:text-brand-yellow/80' : ''].join(' ')}>
      {label}
    </span>
    <span className="text-3xl font-black text-white">{value}</span>
    {sub && <span className="text-gray-500 text-xs mt-1">{sub}</span>}
    {onClick && (
      <span className="text-gray-600 text-xs mt-2 group-hover:text-brand-yellow/60 transition-colors">
        View details &rarr;
      </span>
    )}
  </div>
);

const formatDollar = (cents) => {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-');
  return `${m}/${d}`;
};

const chartTooltipStyle = {
  backgroundColor: '#1a1a2e',
  border: '1px solid #4A5D3A',
  borderRadius: 6,
  color: '#fff',
};

const axisStyle = { fill: '#6B7280', fontSize: 11 };

const SectionHeader = ({ children }) => (
  <h3 className="text-brand-yellow font-bold uppercase tracking-wider text-sm mb-4">
    {children}
  </h3>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-24" data-testid="analytics-loading">
    <div className="w-8 h-8 border-2 border-army border-t-brand-yellow rounded-full animate-spin" />
  </div>
);

const ErrorState = ({ message }) => (
  <div
    className="flex items-center justify-center py-24 text-red-400"
    data-testid="analytics-error"
  >
    {message || 'Failed to load analytics. Please try again.'}
  </div>
);

// drilldown values:
//   null         => show the main analytics overview
//   'purchases'  => show the purchases detail table
const AnalyticsDashboard = ({ onNavigate, onOpenSalesByVideo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminAnalyticsAPI
      .get()
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load analytics.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  // Show purchases drill-down when the user clicked Revenue or Purchases cards
  if (drilldown === 'purchases') {
    return (
      <PurchasesDetail onBack={() => setDrilldown(null)} />
    );
  }

  const { totals, users_by_role, signups_last_30d, revenue_last_30d, votes_last_30d, top_videos, top_rappers } = data;

  const roleChartData = [
    { name: 'Viewers', value: users_by_role?.viewer || 0 },
    { name: 'Artists', value: users_by_role?.artist || 0 },
    { name: 'Admins', value: users_by_role?.admin || 0 },
  ];

  const signupsChartData = (signups_last_30d || []).map((d) => ({
    ...d,
    label: formatDate(d.date),
  }));
  const revenueChartData = (revenue_last_30d || []).map((d) => ({
    ...d,
    dollars: d.cents / 100,
    label: formatDate(d.date),
  }));
  const votesChartData = (votes_last_30d || []).map((d) => ({
    ...d,
    label: formatDate(d.date),
  }));

  return (
    <div className="space-y-8" data-testid="analytics-dashboard">
      {onOpenSalesByVideo && (
        <button
          type="button"
          onClick={onOpenSalesByVideo}
          className="group sales-card sales-card-lit w-full flex items-center justify-between gap-4 text-left hover:border-army-light transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow/60"
          data-testid="open-sales-by-video"
        >
          <span>
            <span className="sales-heading">Analytics by video</span>
            <span className="block text-gray-400 text-sm mt-1">Each video's share of sales and revenue, for any date range.</span>
          </span>
          <span className="text-gray-500 text-xl transition-[color,transform] duration-150 motion-reduce:transition-none group-hover:text-brand-yellow group-hover:translate-x-0.5" aria-hidden="true">&rarr;</span>
        </button>
      )}
      {/* Overview stat cards */}
      <div>
        <SectionHeader>Overview</SectionHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            label="Total Users"
            value={totals?.users?.toLocaleString() ?? '0'}
            onClick={onNavigate ? () => onNavigate('users') : undefined}
          />
          <StatCard
            label="Artists"
            value={totals?.artists?.toLocaleString() ?? '0'}
          />
          <StatCard
            label="Videos"
            value={totals?.videos?.toLocaleString() ?? '0'}
            onClick={onNavigate ? () => onNavigate('videos') : undefined}
          />
          <StatCard label="Total Views" value={totals?.total_views?.toLocaleString() ?? '0'} />
          <StatCard label="Total Votes" value={totals?.total_votes?.toLocaleString() ?? '0'} />
          <StatCard label="Comments" value={totals?.total_comments?.toLocaleString() ?? '0'} />
          <StatCard label="Likes" value={totals?.total_likes?.toLocaleString() ?? '0'} />
          <StatCard
            label="Purchases"
            value={totals?.total_purchases?.toLocaleString() ?? '0'}
            onClick={() => setDrilldown('purchases')}
          />
          <StatCard
            label="Revenue"
            value={formatDollar(totals?.total_revenue_cents)}
            sub="all time"
            onClick={() => setDrilldown('purchases')}
          />
        </div>
      </div>

      {/* Line charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Signups */}
        <div className="bg-battle-gray border border-army/20 rounded-lg p-5" data-testid="chart-signups">
          <SectionHeader>Signups (last 30 days)</SectionHeader>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={signupsChartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
              <YAxis tick={axisStyle} allowDecimals={false} />
              <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: YELLOW }} />
              <Line
                type="monotone"
                dataKey="count"
                stroke={YELLOW}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: YELLOW }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue */}
        <div className="bg-battle-gray border border-army/20 rounded-lg p-5" data-testid="chart-revenue">
          <SectionHeader>Revenue (last 30 days)</SectionHeader>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={revenueChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
              <YAxis tick={axisStyle} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={chartTooltipStyle}
                labelStyle={{ color: YELLOW }}
                formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']}
              />
              <Line
                type="monotone"
                dataKey="dollars"
                stroke={MUTED_GREEN}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: MUTED_GREEN }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Votes */}
        <div className="bg-battle-gray border border-army/20 rounded-lg p-5" data-testid="chart-votes">
          <SectionHeader>Votes (last 30 days)</SectionHeader>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={votesChartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
              <YAxis tick={axisStyle} allowDecimals={false} />
              <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: YELLOW }} />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#A78BFA"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#A78BFA' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Users by role pie + top tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pie chart */}
        <div className="bg-battle-gray border border-army/20 rounded-lg p-5" data-testid="chart-roles">
          <SectionHeader>Users by Role</SectionHeader>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={roleChartData}
                cx="50%"
                cy="45%"
                innerRadius={55}
                outerRadius={80}
                dataKey="value"
                paddingAngle={2}
              >
                {roleChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend
                wrapperStyle={{ fontSize: 12, color: '#9CA3AF' }}
                formatter={(value) => <span style={{ color: '#9CA3AF' }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Videos */}
        <div className="bg-battle-gray border border-army/20 rounded-lg p-5" data-testid="top-videos-table">
          <SectionHeader>Top Videos by Views</SectionHeader>
          {top_videos && top_videos.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase">
                  <th className="text-left pb-2 font-semibold">Title</th>
                  <th className="text-right pb-2 font-semibold">Views</th>
                </tr>
              </thead>
              <tbody>
                {top_videos.map((v) => (
                  <tr key={v.id} className="border-t border-army/10">
                    <td className="py-2 pr-2 text-gray-300 truncate max-w-0 w-full">{v.title}</td>
                    <td className="py-2 text-right text-brand-yellow font-bold whitespace-nowrap">
                      {(v.view_count || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-sm">No videos yet.</p>
          )}
        </div>

        {/* Top Rappers */}
        <div className="bg-battle-gray border border-army/20 rounded-lg p-5" data-testid="top-rappers-table">
          <SectionHeader>Top Rappers by ELO</SectionHeader>
          {top_rappers && top_rappers.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase">
                  <th className="text-left pb-2 font-semibold">Artist</th>
                  <th className="text-right pb-2 font-semibold">ELO</th>
                  <th className="text-right pb-2 font-semibold">W/L</th>
                </tr>
              </thead>
              <tbody>
                {top_rappers.map((r) => (
                  <tr key={r.user_id} className="border-t border-army/10">
                    <td className="py-2 pr-2 text-gray-300 truncate max-w-0 w-full">{r.display_name}</td>
                    <td className="py-2 text-right text-brand-yellow font-bold whitespace-nowrap">
                      {r.elo_rating}
                    </td>
                    <td className="py-2 text-right text-gray-400 whitespace-nowrap">
                      {r.wins}W/{r.losses}L
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-sm">No ranked rappers yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
